import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Paperclip, UploadCloud } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { IconTipB } from '@/components/studio/IconTipB';

const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');

interface DropZoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

export function ComposerDropZone({ onFiles, disabled, className, children }: DropZoneProps) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);

  const onDragEnter = (e: DragEvent) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    depth.current += 1;
    setOver(true);
  };
  const onDragLeave = (e: DragEvent) => {
    if (!hasFiles(e)) return;
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setOver(false);
  };
  const onDragOver = (e: DragEvent) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };
  const onDrop = (e: DragEvent) => {
    if (disabled || !hasFiles(e)) return;
    e.preventDefault();
    depth.current = 0;
    setOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) onFiles(files);
  };

  return (
    <div
      className={cn('relative', className)}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-drop-active={over || undefined}
    >
      {children}
      {over && (
        <div className="pointer-events-none absolute inset-0 z-10 rounded-2xl border-2 border-dashed border-primary/70 bg-white/85 dark:bg-zinc-950/80 flex items-center justify-center gap-2 text-sm font-medium text-accent-foreground">
          <UploadCloud className="h-5 w-5" />
          Drop files: video up to 20 GB, design files up to 2 GB
        </div>
      )}
    </div>
  );
}

interface AttachButtonProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function AttachFilesButton({ onFiles, disabled }: AttachButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <IconTipB label="Attach files" side="top">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="h-8 w-8 p-0 hover:bg-black/5 dark:hover:bg-white/10 rounded-full transition-colors duration-200"
          title="Attach files"
        >
          <Paperclip className="h-[18px] w-[18px] studio-text-secondary" />
        </Button>
      </IconTipB>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        data-testid="attach-input"
        onChange={(e) => {
          if (e.target.files?.length) onFiles(Array.from(e.target.files));
          e.target.value = '';
        }}
      />
    </>
  );
}
